import { DateRange } from "@mui/x-date-pickers-pro";
import { ResponseTypeList } from "src/Pages/Company/Absences/api";
import { IAbsenceGroup } from "src/Pages/Company/Absences/AbsenceGroups/AbsenceGroups.models";
import {
  EPatternUnit,
  EPresenceType,
  IAbsenceTypeConfigurationOptions,
  ICustomFieldDTO,
} from "src/Pages/Company/Absences/AbsenceType/AbsenceType.models";
import { EMPLOYEE_PRESENCE } from "src/Components/Avatar/presenceColor/models/employeePresence.models";
import { EmploymentForm } from "src/Pages/Company/EmployeeManagement/CompanyEmployees/Edit/EmployeeEmployment/constants/employmentForms";

export enum Status {
  ACCEPTED = "ACCEPTED",
  CANCELLED = "CANCELLED",
  WAITING_FOR_APPROVAL = "WAITING_FOR_APPROVAL",
  CORRECTION_REQUIRED = "CORRECTION_REQUIRED",
  REJECTED = "REJECTED",
}

export const StatusOptions = [
  { value: Status.ACCEPTED, label: "Zaakceptowany" },
  {
    value: Status.WAITING_FOR_APPROVAL,
    label: "Oczekuje na akceptację",
  },
  { value: Status.CORRECTION_REQUIRED, label: "Wymagana zmiana" },
  { value: Status.CANCELLED, label: "Anulowany" },
  { value: Status.REJECTED, label: "Odrzucony" },
];

export enum ApproverType {
  IMMEDIATE_SUPERIOR = "IMMEDIATE_SUPERIOR",
  INTERMEDIATE_SUPERIOR = "INTERMEDIATE_SUPERIOR",
  AHR = "AHR",
}

export enum HistoryAction {
  CREATION = "CREATION",
  EDITION = "EDITION",
  CANCELLATION = "CANCELLATION",
  CANCELLATION_AFTER_CALENDAR_CHANGE = "CANCELLATION_AFTER_CALENDAR_CHANGE",
  ACCEPTANCE = "ACCEPTANCE",
  REJECTION = "REJECTION",
  REQUIRE_CORRECTION = "REQUIRE_CORRECTION",
}

export enum RequesterType {
  AHR = "AHR",
  OMB = "OMB",
  EMPLOYEE = "EMPLOYEE",
  UNKNOWN = "UNKNOWN",
  SYSTEM = "SYSTEM",
}

export interface IAbsenceTypeInfoRequest {
  id: string;
  name: string;
  description?: string;
  presenceType: EPresenceType;
  configurationOptions: Pick<
    IAbsenceTypeConfigurationOptions,
    | "billInHours"
    | "billInCalendarDays"
    | "multipleRequestsPerDayAllowed"
    | "modificationAllowed"
  >;
}

interface IAbsence {
  employeeId: string;
  date: string;
  absenceTypeId: string;
  absenceName: string;
  absenceRequestId: string;
  status: Status;
  absenceGroupId: string;
  isEffective: boolean;
  presenceType: EPresenceType;
}

export interface IEmployeeAbsences {
  absences: IAbsence[];
  absenceGroupInfos: IAbsenceGroup[];
  absenceTypeInfos: IAbsenceTypeInfoRequest[];
}

export interface IAbsenceRequestDay {
  date: string;
  absenceName: string;
  bgColor: string;
  color: string;
  absenceRequestId: string;
  multipleRequestsPerDayAllowed: boolean;
}

// TODO: cleanup types - after stabilizing API merge Employee interface into one
export interface IEmployeeBase {
  employeeId: string;
  firstName: string;
  lastName: string;
}

export interface IEmployeeListItem extends IEmployeeBase {
  type: ApproverType;
}

export interface IEmployeeDetails extends IEmployeeBase {
  fkNumber: string;
  avatarUrl?: string;
  initials: string;
  area: string | null;
  parentArea: string | null;
  additionalDisplayArea: string | null;
  localization: string | null;
  presenceStatus: EMPLOYEE_PRESENCE;
  position: string | null;
  dailyWorkingHours: number;
  email: string | null;
  phone: string | null;
  presentAtQueriedTimeRange?: boolean;
  employmentType: EmploymentForm | null;
}

export interface IEmployeesAutocomplete {
  name: string;
  label: string;
  enabled: boolean;
  absenceTypeId?: string;
  queryKey: string;
  queryFn: ({
    params,
  }: Record<string, unknown>) => Promise<ResponseTypeList<IEmployeeDetails>>;
  additionalQueryParams?: { [key: string]: string };
  required?: boolean;
  hasDefaultValue?: boolean;
  setFirstAsDefault?: boolean;
  disabled?: boolean;
}

export interface IAbsenceRequestFormValues {
  absenceType: string;
  unit: EPatternUnit;
  dates: DateRange<Date>;
  date: Date | null;
  startTime: number;
  endTime: number;
  approver: IEmployeeDetails;
  replacer: IEmployeeDetails | null;
  comment: string;
  justification: string;
  attachments: { id: string }[];
}

export interface AbsenceTypeCycleDay {
  type: EPatternUnit;
  dates: string[];
}

export interface AbsenceTypeCycleHour {
  type: EPatternUnit;
  date: string;
  startTime: number;
  endTime: number;
}

export interface IAbsenceRequestDTO {
  absenceTypeId: string;
  employeeId: string;
  absences: AbsenceTypeCycleDay | AbsenceTypeCycleHour;
  approverId: string;
  replacerId?: string;
  justification: string;
  comment: string;
}

export interface IRequireCorrectionDTO {
  note: string;
}

export interface IAbsenceTimes {
  date: string;
  endTime?: string;
  startTime?: string;
}

export interface IAbsenceRequestRange {
  start: {
    date: string;
    time: string | null;
  };
  end: {
    date: string;
    time: string | null;
  };
}

export interface ITimeAmount {
  days: number;
  hours: number;
}

export type CustomFieldValues = Record<string, string | string[] | boolean>;

export interface ICustomFieldsConfiguration {
  customFields: ICustomFieldDTO[];
}

interface IAbsenceRequestBase {
  absenceRequestId: string;
  absenceType: {
    absenceTypeId: string;
    name: string;
    description?: string;
    configurationOptions: Pick<
      IAbsenceTypeConfigurationOptions,
      "modificationAllowed"
    >;
    customFieldsConfiguration: ICustomFieldsConfiguration;
  };
  actionsAllowed: {
    editable: boolean;
    approvable: boolean;
    rejectable: boolean;
    cancelable: boolean;
    sendForCorrectionEligible: boolean;
  };
  type: EPatternUnit;
  absenceTimes: IAbsenceTimes[];
  effectiveAbsenceTimes: IAbsenceTimes[];
  effectiveTimeAmount: ITimeAmount;
  totalTimeAmount: ITimeAmount;
  status: Status;
}

export interface IAbsenceRequest extends IAbsenceRequestBase {
  employee: {
    employeeId: string;
  };
  approver: IEmployeeDetails;
  replacer: IEmployeeDetails;
  justification: string;
  attachments?: {
    attachmentId: string;
    fileName: string;
  }[];
  note: {
    creationDate: string;
    creator: IEmployeeBase;
    content: string;
  };
  comment: string;
  customFieldValues: CustomFieldValues;
}

export interface IEmployeeAbsenceRequestListItem extends IAbsenceRequestBase {
  employee: IEmployeeDetails;
  range: IAbsenceRequestRange;
  // TODO: cleanup types - after stabilizing API merge Employee interface into one
  approvedBy: IEmployeeBase;
  approver: IEmployeeBase;
  creationTime: string;
  acceptationTime: string;
  rejectionTime: string;
  cancellationTime: string;
}

export interface IAbsenceRequestRequireCorrectionFormValues {
  note: string;
}

interface IAbsenceRequestHistoryChange<T> {
  before: T;
  after: T;
}

export enum AbsenceRequestHistoryEntryDetailsTypes {
  TYPE = "type",
  RANGE = "range",
  TIME_AMOUNT = "timeAmount",
  APPROVER = "approver",
  REPLACER = "replacer",
  ATTACHMENTS = "attachments",
  JUSTIFICATION = "justification",
  COMMENT = "comment",
  NOTE = "note",
}

export const HistoryEntryDetailsLabels = {
  [AbsenceRequestHistoryEntryDetailsTypes.TYPE]: "Tryb wniosku",
  [AbsenceRequestHistoryEntryDetailsTypes.RANGE]: "Termin wniosku",
  [AbsenceRequestHistoryEntryDetailsTypes.TIME_AMOUNT]:
    "Liczba dni/godzin wniosku",
  [AbsenceRequestHistoryEntryDetailsTypes.APPROVER]: "Osoba akceptująca",
  [AbsenceRequestHistoryEntryDetailsTypes.REPLACER]: "Osoba zastępująca",
  [AbsenceRequestHistoryEntryDetailsTypes.ATTACHMENTS]: "Załączniki",
  [AbsenceRequestHistoryEntryDetailsTypes.JUSTIFICATION]: "Uzasadnienie",
  [AbsenceRequestHistoryEntryDetailsTypes.COMMENT]: "Komentarz",
  [AbsenceRequestHistoryEntryDetailsTypes.NOTE]: "Notatka",
};

export interface IAbsenceRequestHistoryEntryDetails {
  [AbsenceRequestHistoryEntryDetailsTypes.TYPE]: IAbsenceRequestHistoryChange<EPatternUnit> | null;
  [AbsenceRequestHistoryEntryDetailsTypes.RANGE]: IAbsenceRequestHistoryChange<IAbsenceRequestRange> | null;
  [AbsenceRequestHistoryEntryDetailsTypes.TIME_AMOUNT]: IAbsenceRequestHistoryChange<ITimeAmount> | null;
  [AbsenceRequestHistoryEntryDetailsTypes.APPROVER]: IAbsenceRequestHistoryChange<IEmployeeDetails> | null;
  [AbsenceRequestHistoryEntryDetailsTypes.REPLACER]: IAbsenceRequestHistoryChange<IEmployeeDetails> | null;
  [AbsenceRequestHistoryEntryDetailsTypes.JUSTIFICATION]: IAbsenceRequestHistoryChange<string> | null;
  [AbsenceRequestHistoryEntryDetailsTypes.ATTACHMENTS]: IAbsenceRequestHistoryChange<
    string[]
  > | null;
}

export type AbsenceRequestHistoryTableValue =
  | EPatternUnit
  | IAbsenceRequestRange
  | ITimeAmount
  | IEmployeeDetails
  | string
  | string[]
  | null;
export interface IAbsenceRequestHistoryTableEntry {
  id: AbsenceRequestHistoryEntryDetailsTypes;
  before: AbsenceRequestHistoryTableValue;
  after: AbsenceRequestHistoryTableValue;
}

export interface IAbsenceRequestHistoryEntry {
  action: HistoryAction;
  status: Status;
  requester: {
    type: RequesterType;
    firstName: string;
    lastName: string;
    metadata: object;
  };
  actionTime: string;
  details: IAbsenceRequestHistoryEntryDetails | null;
  order: number;
}
export interface IAbsenceRequestHistory {
  entries: IAbsenceRequestHistoryEntry[];
}

interface IAbsencePlanEntry {
  from: string;
  to: string;
  absenceTypeId: string | null;
}
export interface IAbsencePlan {
  employeeId: string;
  year: number;
  entries: IAbsencePlanEntry[];
}
